import { useState, type ReactNode } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ErrorState, TableSkeleton } from '@/components/states'
import { PdfButton } from '@/components/billing/PdfButton'
import type { Invoice } from '@/api/types'
import { centsLabel } from '@/lib/billing'
import { formatAbsolute } from '@/lib/format'
import { invoiceLabel, invoiceStatus, isPayable, kindLabel } from '@/lib/invoices'

import {
  downloadAccountInvoicePdf,
  type ResendInvoiceResult,
  type RetryInvoiceResult,
  type VoidInvoiceResult,
  resendInvoice,
  retryInvoice,
  useStaffInvoices,
  useStaffMutation,
  voidInvoice,
} from './api'
import { useStaff } from './hooks'
import { ReasonDialog } from './ui'

type Outcome =
  | ({ kind: 'resend' } & ResendInvoiceResult)
  | ({ kind: 'void' } & VoidInvoiceResult)
  | ({ kind: 'retry' } & RetryInvoiceResult)

type Action = { kind: 'resend' | 'void' | 'retry'; invoice: Invoice }

/** What each action does, said before the reason is asked for. */
const COPY: Record<Action['kind'], { title: string; confirm: string; body: (number: string) => ReactNode; destructive: boolean }> = {
  resend: {
    title: 'Resend the invoice email',
    confirm: 'Resend email',
    destructive: false,
    body: (n) => <>Sends the invoice email for <strong>{n}</strong> again to every administrator and billing member of the account. No attachment, a link only.</>,
  },
  void: {
    title: 'Void the invoice',
    confirm: 'Void invoice',
    destructive: true,
    body: (n) => <>Voids the open invoice <strong>{n}</strong>. It can no longer be paid and cannot be undone. The customer is told.</>,
  },
  retry: {
    title: 'Retry payment',
    confirm: 'Charge the default card',
    destructive: false,
    body: (n) => <>Charges the open invoice <strong>{n}</strong> to the customer's default card now. The result is shown here either way.</>,
  },
}

function PageRows({
  accountId,
  cursor,
  last,
  onMore,
  onAction,
}: {
  accountId: string
  cursor: string | null
  last: boolean
  onMore: (cursor: string) => void
  onAction: (action: Action) => void
}) {
  const staff = useStaff()
  const page = useStaffInvoices(accountId, cursor)
  if (page.isPending) {
    return (
      <TableRow>
        <TableCell colSpan={8}>
          <TableSkeleton rows={2} columns={4} />
        </TableCell>
      </TableRow>
    )
  }
  if (page.isError) {
    return (
      <TableRow>
        <TableCell colSpan={8}>
          <ErrorState error={page.error} title="Could not load invoices" onRetry={() => void page.refetch()} />
        </TableCell>
      </TableRow>
    )
  }
  return (
    <>
      {page.data.invoices.map((invoice) => {
        const status = invoiceStatus(invoice.status)
        const open = isPayable(invoice)
        return (
          <TableRow key={invoice.id} data-testid="staff-invoice-row" data-status={invoice.status}>
            <TableCell className="font-mono text-xs">{invoiceLabel(invoice)}</TableCell>
            <TableCell className="text-xs whitespace-nowrap">{formatAbsolute(invoice.created)}</TableCell>
            <TableCell><Badge variant="outline">{kindLabel(invoice.kind)}</Badge></TableCell>
            <TableCell><Badge variant={status.tone}>{status.label}</Badge></TableCell>
            <TableCell className="text-right tabular-nums">{centsLabel(invoice.total_cents)}</TableCell>
            <TableCell className="text-right tabular-nums">{invoice.amount_refunded_cents > 0 ? centsLabel(invoice.amount_refunded_cents) : '—'}</TableCell>
            <TableCell className="text-right tabular-nums">{open ? centsLabel(invoice.amount_due_cents) : '—'}</TableCell>
            <TableCell className="text-right">
              <span className="inline-flex flex-wrap justify-end gap-2">
                <PdfButton
                  path={invoice.id}
                  fallbackName={`${invoiceLabel(invoice)}.pdf`}
                  fetcher={() => downloadAccountInvoicePdf(accountId, invoice.id)}
                  testId="staff-invoice-pdf"
                />
                {staff.can('invoice_resend') ? (
                  <Button size="sm" variant="outline" data-testid="staff-invoice-resend" onClick={() => { onAction({ kind: 'resend', invoice }) }}>
                    Resend
                  </Button>
                ) : null}
                {open && staff.can('billing_plan_quota') ? (
                  <>
                    <Button size="sm" variant="outline" data-testid="staff-invoice-retry" onClick={() => { onAction({ kind: 'retry', invoice }) }}>
                      Retry payment
                    </Button>
                    <Button size="sm" variant="destructive" data-testid="staff-invoice-void" onClick={() => { onAction({ kind: 'void', invoice }) }}>
                      Void
                    </Button>
                  </>
                ) : null}
              </span>
            </TableCell>
          </TableRow>
        )
      })}
      {page.data.invoices.length === 0 && cursor === null ? (
        <TableRow>
          <TableCell colSpan={8} className="text-sm text-muted-foreground">No invoices.</TableCell>
        </TableRow>
      ) : null}
      {last && page.data.has_more && page.data.next_cursor !== null ? (
        <TableRow>
          <TableCell colSpan={8} className="text-center">
            <Button size="sm" variant="outline" onClick={() => { onMore(page.data.next_cursor as string) }}>Load more</Button>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  )
}

/**
 * The account's invoices for staff (BI14): view and download from SUPPORT, resend from OPERATOR,
 * void and retry from SUPEROPERATOR (BI15). Every write asks for a reason and is audited server-side.
 */
export function StaffInvoicesCard({
  accountId,
  onDone,
}: {
  accountId: string
  onDone: (title: string, notified?: boolean, detail?: ReactNode) => void
}) {
  const [cursors, setCursors] = useState<(string | null)[]>([null])
  const [action, setAction] = useState<Action | null>(null)
  const mutation = useStaffMutation(
    async ({ kind, invoiceId, reason }: { kind: Action['kind']; invoiceId: string; reason: string }): Promise<Outcome> => {
      if (kind === 'resend') return { kind, ...(await resendInvoice(accountId, invoiceId, reason)) }
      if (kind === 'void') return { kind, ...(await voidInvoice(accountId, invoiceId, reason)) }
      return { kind, ...(await retryInvoice(accountId, invoiceId, reason)) }
    },
  )

  return (
    <Card data-testid="staff-invoices">
      <CardHeader>
        <CardTitle>Invoices</CardTitle>
        <CardDescription>Newest first. Downloads, resends, voids and retries are all audited.</CardDescription>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Invoice</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Kind</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="text-right">Refunded</TableHead>
              <TableHead className="text-right">Due</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {cursors.map((cursor, index) => (
              <PageRows
                key={cursor ?? 'first'}
                accountId={accountId}
                cursor={cursor}
                last={index === cursors.length - 1}
                onMore={(next) => { setCursors((current) => [...current, next]) }}
                onAction={setAction}
              />
            ))}
          </TableBody>
        </Table>
      </CardContent>
      {action === null ? null : (
        <ReasonDialog
          title={COPY[action.kind].title}
          description={COPY[action.kind].body(invoiceLabel(action.invoice))}
          confirmLabel={COPY[action.kind].confirm}
          destructive={COPY[action.kind].destructive}
          testId={`staff-invoice-${action.kind}-dialog`}
          onClose={() => { setAction(null) }}
          onConfirm={async (reason) => {
            const done = await mutation.mutateAsync({ kind: action.kind, invoiceId: action.invoice.id, reason })
            const label = invoiceLabel(action.invoice)
            if (done.kind === 'retry') {
              onDone(
                done.result === 'paid' ? `Invoice ${label} paid` : `Invoice ${label} was declined`,
                done.notified,
                done.result === 'paid' ? undefined : <>The default card was declined{done.decline_code === null ? '' : ` (${done.decline_code})`}. The invoice is still open.</>,
              )
            } else if (done.kind === 'resend') {
              onDone(`Invoice email sent to ${done.recipients} ${done.recipients === 1 ? 'person' : 'people'}`, done.notified)
            } else {
              onDone(`Invoice ${label} voided`, done.notified)
            }
          }}
        />
      )}
    </Card>
  )
}
